
import React from 'react';
import { ExternalLink, Clock, Users, Star, ArrowRight } from 'lucide-react';

const FeaturedDeals: React.FC = () => {
  const deals = [
    {
      id: 1,
      title: "Amazon Prime Day Extended",
      description: "Up to 70% off electronics, home goods, and more",
      discount: "70%",
      retailer: "Amazon",
      expiresIn: "2 days",
      users: "2,341",
      rating: 4.8,
      image: "https://images.pexels.com/photos/4386366/pexels-photo-4386366.jpeg?auto=compress&cs=tinysrgb&w=400"
    },
    {
      id: 2,
      title: "Nike Flash Sale",
      description: "Athletic wear and sneakers at unbeatable prices",
      discount: "50%",
      retailer: "Nike",
      expiresIn: "5 hours",
      users: "1,892",
      rating: 4.9,
      image: "https://images.pexels.com/photos/2529148/pexels-photo-2529148.jpeg?auto=compress&cs=tinysrgb&w=400"
    },
    {
      id: 3,
      title: "Best Buy Tech Deals",
      description: "Laptops, phones, and gaming gear on sale",
      discount: "60%",
      retailer: "Best Buy",
      expiresIn: "1 day",
      users: "3,156",
      rating: 4.7,
      image: "https://images.pexels.com/photos/356056/pexels-photo-356056.jpeg?auto=compress&cs=tinysrgb&w=400"
    },
    {
      id: 4,
      title: "Target Home & Garden",
      description: "Furniture, decor, and outdoor essentials",
      discount: "45%",
      retailer: "Target",
      expiresIn: "3 days",
      users: "987",
      rating: 4.6,
      image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=400"
    },
    {
      id: 5,
      title: "Walmart Grocery Deals",
      description: "Fresh produce, pantry staples, and household items",
      discount: "35%",
      retailer: "Walmart",
      expiresIn: "6 days",
      users: "4,523",
      rating: 4.5,
      image: "https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg?auto=compress&cs=tinysrgb&w=400"
    },
    {
      id: 6,
      title: "Macy's Fashion Week",
      description: "Designer clothing and accessories",
      discount: "65%",
      retailer: "Macy's",
      expiresIn: "4 days",
      users: "1,234",
      rating: 4.8,
      image: "https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg?auto=compress&cs=tinysrgb&w=400"
    }
  ];

  const scrollToSignup = () => {
    const element = document.getElementById('signup');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="deals" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Today's Hottest Deals
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Hand-picked and verified by our deal experts. These exclusive offers won't last long!
          </p>
          <div className="inline-flex items-center space-x-2 bg-red-100 text-red-800 px-4 py-2 rounded-full font-medium">
            <Clock className="w-4 h-4" />
            <span>Updated every hour • Limited time offers</span>
          </div>
        </div>

        {/* Deals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {deals.map((deal, index) => (
            <div key={deal.id} className="card overflow-hidden group animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
              <div className="relative">
                <img 
                  src={deal.image} 
                  alt={deal.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full font-bold">
                  {deal.discount} OFF
                </div>
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-sm font-medium">
                  {deal.retailer}
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                  {deal.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {deal.description}
                </p>
                
                <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>Expires in {deal.expiresIn}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4" />
                    <span>{deal.users} used</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm font-medium">{deal.rating}</span>
                  </div>
                  <button 
                    onClick={scrollToSignup}
                    className="flex items-center space-x-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm group"
                  >
                    <span>Get Deal</span>
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Don't Miss Out on Tomorrow's Deals!
          </h3>
          <p className="text-xl mb-8 text-green-100 max-w-2xl mx-auto">
            Join our community and get exclusive access to deals before they go public. 
            Plus, receive personalized recommendations based on your shopping preferences.
          </p>
          <button 
            onClick={scrollToSignup}
            className="bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center space-x-2 group"
          >
            <span>Join Free & Get Instant Access</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedDeals;
